import { 
  FileText, 
  Brain, 
  Settings, 
  Share2, 
  BarChart3, 
  Shield,
  ArrowRight
} from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";

const features = [
  {
    icon: FileText,
    title: "Multi-Format Upload",
    description: "Upload PDFs, Word docs, images, or scanned documents. Our AI handles them all seamlessly.",
    slug: "document-upload"
  },
  {
    icon: Brain,
    title: "AI-Powered Extraction",
    description: "Advanced AI automatically identifies questions, answer choices, and question types with 98% accuracy.",
    slug: "ai-extraction"
  },
  {
    icon: Settings,
    title: "Smart Question Detection",
    description: "Automatically detects multiple choice, rating scales, matrix questions, and more.",
    slug: "question-types"
  },
  {
    icon: Share2,
    title: "Easy Distribution",
    description: "Share via link, embed on your site, or send email invitations. Track responses in real-time.",
    slug: "response-collection"
  },
  {
    icon: BarChart3,
    title: "Powerful Analytics",
    description: "Visualize responses with charts, export to CSV/Excel, and gain insights instantly.",
    slug: "analytics-export"
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "SOC2 compliant, GDPR ready, with 256-bit encryption. Your data is always protected.",
    slug: "team-collaboration"
  },
];

export function Features() {
  return (
    <Section>
      <Container>
        <AnimatedSection className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Powerful <span className="text-gradient">Features</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need to transform documents into professional surveys
          </p>
        </AnimatedSection>
        
        {/* Responsive grid with stagger animation */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={staggerItem}>
              <Card className="hover-lift h-full group">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                  <CardDescription className="text-muted-foreground mb-4">
                    {feature.description}
                  </CardDescription>
                  <Link 
                    to={`/features#${feature.slug}`}
                    className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                  >
                    Learn about {feature.title.toLowerCase()}
                    <ArrowRight className="ml-1 h-4 w-4 group-hover/link:translate-x-0.5 transition-transform" />
                  </Link>
                </CardHeader>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
